// Shared role + module-permission map used by both Layout sidebar and route guard.
// Add new modules here and they'll be honored everywhere.

// Allowed routes for non-admin primary roles.
// Note: every non-admin role gets `/expenses` so they can claim reimbursements.
// Backend scopes the data to only their own expenses (see GET /api/expenses).
export const ROLE_PATHS = {
  proposals_user: ['/proposals', '/proposals/builder', '/clients', '/leads',
                   '/my/dashboard', '/my/tasks', '/my/attendance', '/my/leaves', '/my/permissions', '/standup', '/client-workboard', '/chat',
                   '/appointments', '/reminders', '/expenses'],
  attendance_user: ['/attendance', '/import-attendance', '/leaves', '/checkin-dashboard',
                    '/my/dashboard', '/my/tasks', '/my/attendance', '/my/leaves', '/my/permissions', '/standup', '/client-workboard',
                    '/permissions', '/chat', '/appointments', '/reminders', '/expenses'],
  staff: ['/my/dashboard', '/my/tasks', '/my/attendance', '/my/leaves', '/my/permissions', '/standup', '/client-workboard', '/chat',
          '/appointments', '/reminders', '/expenses'],
  team_lead: ['/my/dashboard', '/my/tasks', '/my/attendance', '/my/leaves', '/my/permissions',
              '/projects', '/tasks/board', '/standup', '/client-workboard', '/permissions', '/chat',
              '/appointments', '/reminders', '/expenses'],
  project_coordinator: ['/my/dashboard', '/my/tasks', '/my/attendance', '/my/leaves', '/my/permissions',
                        '/projects', '/tasks/board', '/standup', '/client-workboard', '/permissions', '/chat',
                        '/appointments', '/reminders', '/expenses',
                        '/csrm', '/clients', '/leads'],
  marketing_manager: ['/my/dashboard', '/my/tasks', '/my/attendance', '/my/leaves', '/my/permissions',
                      '/standup', '/client-workboard', '/chat',
                      '/appointments', '/reminders', '/expenses',
                      '/marketing', '/clients', '/leads', '/csrm'],
};

// Optional module flags (granted via Users page checkboxes) that ADD to the user's role-based paths.
export const MODULE_PATHS = {
  proposals: ['/proposals', '/proposals/builder'],
  sales: ['/leads', '/clients'],
  attendance: ['/attendance', '/import-attendance', '/leaves', '/checkin-dashboard'],
  vault: ['/vault'],
  projects: ['/projects', '/tasks/board'],
  hostings: ['/hostings'],
  csrm: ['/csrm', '/clients'],
  dmo: ['/marketing', '/clients'],
  dmo_manager: ['/marketing', '/clients'],  // grants manager-level DMO approve rights
};

export const MODULE_LABELS = {
  proposals: 'Proposals',
  sales: 'Leads CRM & Clients',
  attendance: 'Attendance & Leaves',
  vault: 'Password Vault',
  projects: 'Projects & Task Board',
  hostings: 'Hosting & Domain Renewals',
  csrm: 'Client Success (CSRM)',
  dmo: 'Marketing Ops (DMO)',
  dmo_manager: 'DMO Approver / Manager',
};

// Where each role should land when they hit "/"
export const ROLE_HOME = {
  admin: '/dashboard',
  proposals_user: '/proposals',
  attendance_user: '/attendance',
  team_lead: '/my/dashboard',
  project_coordinator: '/csrm',
  marketing_manager: '/marketing',
  staff: '/my/dashboard',
};

// Compute the full list of paths a user is allowed to visit.
export function allowedPathsFor(user) {
  if (!user) return new Set();
  if (user.role === 'admin') return null;  // null = unrestricted
  let role;
  if (user.role === 'project_coordinator') role = 'project_coordinator';
  else if (user.role === 'marketing_manager') role = 'marketing_manager';
  else role = user.is_team_lead ? 'team_lead' : (user.role || 'staff');
  const out = new Set(ROLE_PATHS[role] || ROLE_PATHS[user.role] || ROLE_PATHS.staff);
  for (const m of (user.extra_modules || [])) {
    (MODULE_PATHS[m] || []).forEach((p) => out.add(p));
  }
  return out;
}

export function roleHome(role, isTeamLead) {
  if (role === 'project_coordinator') return ROLE_HOME.project_coordinator;
  if (role === 'marketing_manager') return ROLE_HOME.marketing_manager;
  if (isTeamLead && role !== 'admin') return ROLE_HOME.team_lead;
  return ROLE_HOME[role] || '/my/dashboard';
}
